import { action, computed } from "mobx";
import BaseStore from "../../base";
import { v4 as uuid } from "uuid";

import http from "@app/lib/http";
import AbbreviationEntry from "@app/state/model/abbreviation/entry";
import { events } from "@app/lib/store";

import report from "../../report";
import reportDocumentStore from "../../report-document/report-documents";

import listStore from "./list";

const STGR_LOAD = uuid();
const normalizeAbbreviationKey = (value) =>
    String(value || "")
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[^A-Za-z0-9_]/g, "")
        .toUpperCase();
export class Abbreviation extends BaseStore {
    /**
     * Observable store data
     */
    observable() {
        return {
            list: [],
            usage: {},
            nodes: {},
            filter: "",
            loading: false,
            saving: false,
            loaded: false,
        };
    }

    constructor() {
        super();

        // handle abbreviation update events
        events.on("abbreviation.update", () => this.load());
        events.on("abbreviation.updateTemplateValues", (entry) => this.loadTemplateValues(entry));

        events.on("abbreviation.delete", () => this.load());
        events.on("abbreviation.deleteTemplateValues", ({ documentId }) =>
            this.loadTemplateValues({ documentId }),
        );
        events.on("project.unload", () => {
            this.reset();
        });
    }

    /**
     * Return the project id if the currently loaded project
     */
    @computed get project() {
        return report.id;
    }

    /**
     * Delegate usage stats to the shared list store
     */
    @computed get usageStats() {
        return listStore.usageStats;
    }

    /**
     * Usage stats merged with in-memory editor tracking.
     * Backend stats reflect persisted sections; this.usage reflects the current
     * document in real-time (including unsaved changes). Taking the max of both
     * ensures newly inserted abbreviations appear immediately without waiting
     * for the section to be saved.
     */
    @computed get mergedUsageStats() {
        const merged = {};

        for (const [name, stat] of Object.entries(listStore.usageStats)) {
            merged[name] = { ...stat };
        }

        for (const [name, count] of Object.entries(this.usage)) {
            if (count > 0) {
                if (!merged[name]) {
                    merged[name] = { count: 0, documents: [] };
                }
                if (count > merged[name].count) {
                    merged[name] = { ...merged[name], count };
                }
            }
        }

        return merged;
    }

    /**
     * Return the project id if the currently loaded project
     */
    @computed get entryMap() {
        const map = {};
        this.list.map((entry) => {
            if (entry.name) {
                map[entry.name] = entry;
                const normalized = normalizeAbbreviationKey(entry.name);
                if (normalized && !map[normalized]) {
                    map[normalized] = entry;
                }
            }
        });

        return map;
    }

    /**
     * Return the project id if the currently loaded project
     */
    @computed get regExParts() {
        return {
            separator: "[\\s.,;:!?()[\\]{}]|\\b",
            // A hyphen is not a word char, so `\b` in the separator matches right after an
            // abbreviation and would detect "ABD-". An underscore is a word char, so it never
            // produces that boundary - hence hyphens needing an explicit opt-out here.
            trailing: "(?!-)",
            list: this.list
                .map(({ name }) => name)
                .sort((a, b) => b.length - a.length)
                .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
                .join("|"),
        };
    }

    /**
     * Return the project id if the currently loaded project
     */
    @computed get regEx() {
        const { separator, list, trailing } = this.regExParts;
        return new RegExp(`(?<=${separator}|^)(?:${list})(?=(?:${separator}|$))${trailing}`, "g");
    }

    /**
     * Return the project id if the currently loaded project
     */
    @computed get regExEol() {
        const { separator, list } = this.regExParts;
        return new RegExp(`(?<=${separator}|^)(?:${list})(?=$)`, "g");
    }

    /**
     * Return the project id if the currently loaded project
     */
    @computed get filteredList() {
        const tokens = this.filter
            .split(/\s+/g)
            .filter((token) => !!token)
            .map((token) => token.toLowerCase());

        if (tokens.length === 0) {
            return this.list;
        }

        return this.list.filter((entry) => {
            const name = entry.name?.toLowerCase() || "";
            const text = entry.text?.toLowerCase() || "";

            return tokens.every((token) => {
                return name.includes(token) || text.includes(token);
            });
        });
    }

    /**
     * Return a list of used abbreviations
     */
    @computed get usedAbbreviations() {
        const result = [];

        for (const id of Object.keys(this.usage)) {
            const count = this.usage[id];
            if (count > 0 && this.entryMap[id]) {
                result.push(this.entryMap[id]);
            }
        }

        // sort by name
        return result.sort((left, right) => {
            const a = left.name.toUpperCase();
            const b = right.name.toUpperCase();

            if (a < b) {
                return -1;
            } else if (a > b) {
                return 1;
            } else {
                return 0;
            }
        });
    }

    /**
     * Return an entry by id. Flags it as being used
     */
    use(key, source) {
        const normalizedKey = normalizeAbbreviationKey(key);
        const entry = this.entryMap[key] || this.entryMap[normalizedKey];

        if (entry && source && !this.nodes[source]) {
            this.nodes[source] = true;
            const usageKey = entry.name || key;
            this.usage[usageKey] = (this.usage[usageKey] ?? 0) + 1;
        }

        return entry;
    }

    unuse(key, source) {
        const normalizedKey = normalizeAbbreviationKey(key);
        const entry = this.entryMap[key] || this.entryMap[normalizedKey];

        if (entry && source && this.nodes[source]) {
            delete this.nodes[source];
            const usageKey = entry.name || key;
            if (this.usage[usageKey] > 0) {
                this.usage[usageKey] -= 1;
            }
        }
    }

    /**
     * Reset the usage stats
     */
    resetUsage() {
        this.usage = {};
        this.nodes = {};
    }

    /**
     * Update the filter
     */
    search(text) {
        this.filter = text || "";
    }

    /**
     * Load the list of abbreviation entries from the backend
     */
    @action
    async load() {
        this.loading = true;

        let { data } = await http.get(`/project/${this.project}/abbreviation`).stagger(STGR_LOAD);

        this.list = data.list.map((entry) => {
            return new AbbreviationEntry(entry);
        });

        this.loading = false;
        this.loaded = true;
    }

    /**
     * Load the list of abbreviation entries from the backend
     */
    @action
    async loadTemplateValues({ documentId }) {
        this.loading = true;
        const { clientId } = reportDocumentStore;

        let { data } = await http
            .get(`/client/${clientId}/template/${documentId}/abbreviation`)
            .stagger(STGR_LOAD);

        this.list = data.list.map((entry) => {
            return new AbbreviationEntry(entry);
        });

        this.loading = false;
        this.loaded = true;
    }

    /**
     * Save the entry
     */
    @action
    async save(data) {
        return await listStore.save(data);
    }

    /**
     * Save the entry for template value
     */
    @action
    async saveTemplateEntry(documentId, data) {
        return await listStore.saveTemplateEntry(documentId, data);
    }

    @action
    async unload() {
        this.list = [];
        this.resetUsage();
        listStore.unload();
    }
}

export default new Abbreviation();
